// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

// Utility for cancelable authenticated API calls to remote Stargate devices
import axios from 'axios';
import { retryAsync } from './retry';

/**
 * Default retry configuration for remote API calls
 * Optimized for UI responsiveness
 */
const RETRY_CONFIG = {
  maxRetries: 2, // Reduced retries for faster feedback (initial + 1 retry)
  initialDelay: 500, // Shorter initial delay
  maxDelay: 2000, // Shorter maximum delay between retries
  timeout: 30000, // Slightly longer timeout but fewer retries
};

/**
 * Create a cancelable remote API request
 * @returns {object} { request: Promise, cancel: Function }
 */
export function createCancelableRemoteGet(ip, apiKey, path) {
  const source = axios.CancelToken.source();

  const request = makeRequestWithProtocolFallback(path, 'GET', ip, apiKey, null, source.token);

  return {
    request,
    cancel: () => source.cancel('Request canceled due to component unmount'),
  };
}

/**
 * Create a cancelable remote POST request
 * @returns {object} { request: Promise, cancel: Function }
 */
export function createCancelableRemotePost(ip, apiKey, path, data) {
  const source = axios.CancelToken.source();

  const request = makeRequestWithProtocolFallback(path, 'POST', ip, apiKey, data, source.token);

  return {
    request,
    cancel: () => source.cancel('Request canceled due to component unmount'),
  };
}

/**
 * Make a request with HTTP only (removed HTTPS fallback)
 * @param {string} path - API endpoint path
 * @param {string} method - HTTP method (GET/POST)
 * @param {string} ip - Device IP address
 * @param {string} apiKey - API key for authentication
 * @param {object} data - Request data (for POST requests)
 * @param {CancelToken} cancelToken - Axios cancel token
 * @returns {Promise<any>} - Response data
 */
async function makeRequestWithProtocolFallback(path, method, ip, apiKey, data = null, cancelToken) {
  const token = localStorage.getItem('token');

  // Only use HTTP protocol (removed HTTPS fallback)
  const url = method === 'POST' ? buildProxyPostUrl(path) : buildProxyGetUrl(path);

  const request = async () => {
    const config = {
      params: {
        ip,
        api_key: apiKey,
        // No need for force_protocol anymore since we only use HTTP
      },
      timeout: RETRY_CONFIG.timeout,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-API-Key': apiKey,
      },
      cancelToken,
    };

    let response;
    if (method === 'POST') {
      if (data && Object.keys(data).length > 0) {
        response = await axios.post(url, data, config);
      } else {
        response = await axios.post(url, undefined, config);
      }
    } else {
      response = await axios.get(url, config);
    }

    return response.data;
  };

  const shouldRetry = (error) => {
    if (!error.response) return true;
    const status = error.response.status;
    return status >= 500 || status === 429;
  };

  const result = await retryAsyncCancelable(
    request,
    RETRY_CONFIG.maxRetries,
    RETRY_CONFIG.initialDelay,
    cancelToken,
    shouldRetry
  );

  return result;
}

// Modified retry function that respects cancellation
async function retryAsyncCancelable(
  fn,
  maxRetries,
  initialDelay,
  cancelToken,
  shouldRetry = () => true
) {
  let lastError;
  let delay = initialDelay;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      // Check if already canceled before attempting
      if (cancelToken && cancelToken.reason) {
        throw new axios.Cancel(cancelToken.reason);
      }

      return await fn();
    } catch (error) {
      // If canceled, don't retry
      if (axios.isCancel(error)) {
        throw error;
      }

      lastError = error;

      // Check if we should retry this error
      if (!shouldRetry(error)) {
        throw error;
      }

      if (i < maxRetries) {
        // Use a promise that can be canceled
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, delay);

          // Check for cancellation during delay
          if (cancelToken) {
            const cancelListener = () => {
              clearTimeout(timeout);
              reject(new axios.Cancel('Retry canceled'));
            };

            // If already canceled
            if (cancelToken.reason) {
              cancelListener();
            } else {
              // Listen for future cancellation
              cancelToken.promise.then(cancelListener);
            }
          }
        });

        delay = Math.min(delay * 2, RETRY_CONFIG.maxDelay);
      }
    }
  }

  throw lastError;
}

// Helper to construct the proxy endpoint URL for GET requests
function buildProxyGetUrl(path) {
  // Remove leading '/api/proxy' or '/api' if present, then prepend '/api/proxy'
  let cleanPath = path;
  if (cleanPath.startsWith('/api/proxy/')) {
    cleanPath = cleanPath.slice(10); // remove '/api/proxy'
  } else if (cleanPath.startsWith('/api/')) {
    cleanPath = cleanPath.slice(4); // remove '/api'
  }
  return `/api/proxy${cleanPath}`;
}

// Helper to construct the proxy endpoint URL for POST requests
function buildProxyPostUrl(path) {
  // Remove leading '/api/proxy' or '/api' if present, then prepend '/api/proxy'
  let cleanPath = path;
  if (cleanPath.startsWith('/api/proxy/')) {
    cleanPath = cleanPath.slice(10); // remove '/api/proxy'
  } else if (cleanPath.startsWith('/api/')) {
    cleanPath = cleanPath.slice(4); // remove '/api'
  }
  return `/api/proxy${cleanPath}`;
}
